import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, Clock, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import BlogSEO from '../components/BlogSEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BlogPost = () => {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedPosts, setRelatedPosts] = useState([]);

  useEffect(() => {
    fetchPost();
    fetchRelatedPosts();
  }, [id]);

  const fetchPost = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/posts/${id}`);
      if (response.data.error) {
        setPost(null);
      } else {
        setPost(response.data);
      }
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch post:', error);
      setPost(null);
      setLoading(false);
    }
  };

  const fetchRelatedPosts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/posts?status=published`);
      // Filter out current post and limit to 3
      const filtered = response.data.filter(p => p.id !== id).slice(0, 3);
      setRelatedPosts(filtered);
    } catch (error) {
      console.error('Failed to fetch related posts:', error);
    }
  };

  const calculateReadTime = (content) => {
    const wordsPerMinute = 200;
    const words = content.split(/\s+/).length;
    const minutes = Math.ceil(words / wordsPerMinute);
    return `${minutes} min read`;
  };

  // Function to parse and format the blog content (same as ServiceDetail.jsx)
  const formatBlogContent = (content) => {
    const sections = content.split('\n\n');
    
    return sections.map((section, index) => {
      const lines = section.split('\n').filter(line => line.trim());
      if (lines.length === 0) return null;
      
      const firstLine = lines[0];
      
      // Check if line is a heading (starts with ##)
      if (firstLine.startsWith('##')) {
        const heading = firstLine.replace(/^##\s*/, '');
        return (
          <h2 key={index} className="text-3xl font-bold mb-6 mt-8" style={{ color: 'var(--text-primary)' }}>
            {heading}
          </h2>
        );
      }
      
      // Check if ALL lines in this section are bullet points (start with *)
      const allBullets = lines.every(line => line.trim().startsWith('*'));
      if (allBullets) {
        return (
          <div key={index} className="mb-6">
            {lines.map((line, lineIndex) => (
              <div key={lineIndex} className="flex items-start gap-3 mb-3">
                <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ background: 'var(--accent-primary)' }}></div>
                <p className="text-lg leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                  {line.replace(/^\*\s*/, '')}
                </p>
              </div>
            ))}
          </div>
        );
      }
      
      // Regular paragraph
      return (
        <p key={index} className="text-lg leading-relaxed mb-6" style={{ color: 'var(--text-body)' }}>
          {section}
        </p>
      );
    });
  };

  if (loading) {
    return (
      <div className="pt-32 pb-20 container text-center">
        <p className="text-lg" style={{ color: 'var(--text-secondary)' }}>Loading article...</p>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="pt-32 pb-20 container text-center">
        <h1 className="heading-2 mb-4">Article Not Found</h1>
        <Link to="/blog" className="btn-primary">
          Back to Blog
        </Link>
      </div>
    );
  }

  return (
    <div>
      {/* SEO Component */}
      <BlogSEO post={post} />
      
      {/* Breadcrumb */}
      <section className="pt-32 pb-8">
        <div className="container">
          <Link to="/blog" className="flex items-center gap-2 body-small" style={{ color: 'var(--text-secondary)' }}>
            <ArrowLeft size={16} />
            Back to Blog
          </Link>
        </div>
      </section>

      {/* Article Header */}
      <section className="pb-8">
        <div className="container max-w-4xl">
          <div className="mb-6">
            <span className="caption px-3 py-1 rounded-full" style={{ background: 'var(--accent-wash)', color: 'var(--accent-text)' }}>
              {post.status}
            </span>
          </div>
          <h1 className="heading-1 mb-6">{post.title}</h1>
          <div className="flex items-center gap-4 body-small" style={{ color: 'var(--text-muted)' }}>
            <span>By {post.author}</span>
            <div className="flex items-center gap-2">
              <Calendar size={16} />
              {new Date(post.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </div>
            <div className="flex items-center gap-2">
              <Clock size={16} />
              {calculateReadTime(post.content)}
            </div>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      {post.featured_image_url && (
        <section className="pb-12">
          <div className="container max-w-4xl">
            <img 
              src={post.featured_image_url} 
              alt={post.title}
              className="w-full h-96 rounded-2xl object-cover"
              onError={(e) => e.target.style.display = 'none'}
            />
          </div>
        </section>
      )}

      {/* Article Content */}
      <article className="pb-20">
        <div className="container max-w-4xl">
          <div className="prose max-w-none">
            {formatBlogContent(post.content)}
            
            {/* FAQ Section */}
            {post.faq && post.faq.length > 0 && (
              <div className="mt-12 border-t border-gray-200 pt-12">
                <h2 className="text-3xl font-bold mb-8" style={{ color: 'var(--text-primary)' }}>
                  📋 Frequently Asked Questions
                </h2>
                <div className="space-y-6">
                  {post.faq.map((item, index) => (
                    <div key={index} className="border-l-4 pl-6 py-2" style={{ borderColor: 'var(--accent-primary)' }}>
                      <h3 className="text-xl font-bold mb-3" style={{ color: 'var(--text-primary)' }}>
                        Q: {item.question}
                      </h3>
                      <p className="text-lg leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                        A: {item.answer}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div className="product-card my-8" style={{ background: 'var(--accent-wash)', border: '1px solid var(--accent-primary)' }}>
              <h3 className="heading-3 mb-3">Need Professional Help?</h3>
              <p className="body-medium mb-4" style={{ color: 'var(--text-body)' }}>
                Don't let pests compromise your home or business. Contact NY Pest & Exterminating for a free consultation and same-day service.
              </p>
              <Link to="/contact" className="btn-primary inline-flex">
                Get Free Quote
              </Link>
            </div>
          </div>
        </div>
      </article>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="py-20" style={{ background: 'var(--bg-section)' }}>
          <div className="container">
            <h2 className="heading-2 mb-8 text-center">Related Articles</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {relatedPosts.map(relatedPost => (
                <Link
                  key={relatedPost.id}
                  to={`/blog/${relatedPost.id}`}
                  className="product-card card-hover-lift"
                >
                  <span className="caption px-3 py-1 rounded-full inline-block mb-3" style={{ background: 'var(--accent-wash)', color: 'var(--accent-text)' }}>
                    {relatedPost.status}
                  </span>
                  <h3 className="heading-3 mb-2" style={{ fontSize: '1rem' }}>{relatedPost.title}</h3>
                  <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                    {relatedPost.content.substring(0, 100).replace(/##|\*/g, '')}...
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default BlogPost;